import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import './style.scss';
import {
	closeModal,
	closeModules,
	deleteTest,
	editTest,
	openModal,
	resetActiveTest,
	resetEditor,
	saveTest,
	setEditorPDF,
	getPDF
} from '../../../redux/actions';
import { Button, FixedButtons, Icon } from '../../../components';
import colors from '../../../utils/colors';
import { redirect } from '../../../redux/actions/app';
import GroupInput from '../../../components/GroupInput';
import PrintModalLayout from '../../Modal/layouts/PrintTest';
import DeleteTestModalLayout from '../../Modal/layouts/DeleteTest';

const TestEditorFooter = () => {
	const state = useSelector((state) => state);
	const dispatch = useDispatch();
	const mode = state.editor.mode;
	const activeTest = state.editor.test;
	const isMobileActive = state.editor.mobileActive;
	const selectedFilters = state.filter.selected;

	const [groupCount, setGroupCount] = useState(1);
	const [isLoading, setLoading] = useState(null);

	const taskCount = activeTest?.tasks.length || 0;

	const testTime = useMemo(() => {
		if (activeTest?.tasks.length > 0) {
			const time = Math.ceil(
				activeTest.tasks.map((task) => Number(task['czas_odp'])).reduce((a, b) => a + b)
			).toString();
			if (time === '1') {
				return time + ' minuta';
			} else if (
				!(Number(time) >= 10 && time.charAt(time.length - 2) === '1') &&
				time.slice(-1) > 1 &&
				time.slice(-1) < 5
			) {
				return time + ' minuty';
			} else {
				return time + ' minut';
			}
		} else {
			return 0;
		}
	}, [activeTest]);

	const handleSaveTest = async () => {
		if (activeTest.tasks?.length > 0) {
			if (activeTest.nazwa.trim() === '') {
				const selectedLevel = `${
					state.filter.levels.find((level) => level.id === selectedFilters.level).poziom
				}`;
				const selectedClasses =
					selectedFilters.classes.length > 0
						? `klasa[${selectedFilters.classes.sort((a, b) => a - b)}]`
						: '';
				const selectedSubjects = selectedFilters.subjects.map((subjectID, index) => {
					const subject = state.filter.subjects[selectedFilters.level].find(
						(subject) => subject.id === subjectID
					);
					return index > 0 ? ` ,${subject.przedmiot}` : subject.przedmiot;
				});
				const selectedTopics = state.filter.topics.filter((topic) =>
					selectedFilters.topics.includes(topic['temat_id'])
				);
				const testName = `Test-${selectedLevel}${
					selectedClasses.length > 0 ? '-' + selectedClasses : ''
				}-[${selectedSubjects}][${
					selectedTopics.length > 0 ? selectedTopics.map((topic) => topic.temat) : 'Wszystko'
				}]`;

				const newTest = {
					...activeTest,
					nazwa: testName,
					wybrane_filtry: selectedFilters,
				};
				dispatch(saveTest(newTest, state.user.id));
			} else {
				const newTest = {
					...activeTest,
					wybrane_filtry: selectedFilters,
				};
				dispatch(saveTest(newTest, state.user.id));
			}
		} else {
			toast.error(<span>Wybrany test jest pusty</span>, {
				closeButton: false,
				autoClose: 2500,
				hideProgressBar: true,
			});
		}
	};

	const handlePrint = (fileType) => {
		setLoading(fileType);
		dispatch(getPDF(activeTest, state.user.id, groupCount, fileType, () => {
			setLoading(null);
		}));
	};

	const handleDeleteTest = () => {
		dispatch(deleteTest(activeTest, state.user.id));
		dispatch(closeModules());
		dispatch(closeModal());
		dispatch(resetActiveTest());
		dispatch(resetEditor());
		dispatch(redirect('/my-tests'));
	};

	const openPrintModal = () => {
		if (activeTest.tasks?.length > 0) {
			dispatch(
				openModal(<PrintModalLayout />, true, () => {
					dispatch(setEditorPDF(null, null));
				})
			);
		} else {
			toast.error(<span>Wybrany test jest pusty</span>, {
				closeButton: false,
				autoClose: 2500,
				hideProgressBar: true,
			});
		}
	};

	const openDeleteModal = () => {
		dispatch(
			openModal(<DeleteTestModalLayout test={activeTest} onDelete={handleDeleteTest} />, false)
		);
	};

	return (
		<footer className={`module-editor-footer ${mode}`}>
			<div className="footer-row">
				<div className="footer-row">
					{mode === 'preview' && (
						<div className="module-editor-group-count">
							<GroupInput
								label="Wskaż liczbę grup"
								value={groupCount}
								min={1}
								max={16}
								onChange={setGroupCount}
							/>
						</div>
					)}
					<div className="module-editor-edit-buttons">
						{mode === 'edit' && (
							<Button color="transparent text-primary" onClick={handleSaveTest}>
								zapisz
								<Icon name="download" width="15px" height="15px" color={colors.green} />
							</Button>
						)}
						{mode === 'preview' && (
							<Button
								color="transparent text-primary"
								className="edit-btn"
								onClick={() => dispatch(editTest(activeTest))}
							>
								<span>Edytuj test</span>
								<Icon name="pencil" width="12px" height="12px" color={colors.green} />
							</Button>
						)}
					</div>
				</div>
				{mode === 'preview' && (
					<div className="footer-row">
						<div className="module-editor-test-info">
							<span>Czas: {testTime},</span> <span>Zadań: {taskCount}</span>
						</div>
						<div className="module-editor-print-buttons">
							<Button
								color="secondary text-white"
								className={isLoading === "key" ? ' disabled' : ''}
								onClick={() => handlePrint('key')}
							>
								pobierz i wydrukuj klucz odpowiedzi
								{isLoading === "key" ? (
									<Icon name="spinner" width="13" height="13" />
								) : (
									<Icon name="print" width="15" height="15" color={colors.white} />
								)}
							</Button>
							<Button
								color="primary text-white"
								className={isLoading === "test" ? ' disabled' : ''}
								onClick={() => handlePrint('test')}
							>
								pobierz i drukuj test
								{isLoading === "test" ? (
									<Icon name="spinner" width="13" height="13" />
								) : (
									<Icon name="print" width="15" height="15" color={colors.white} />
								)}
							</Button>
						</div>
					</div>
				)}
			</div>
			{isMobileActive && (
				<FixedButtons>
					<Button color="primary text-white" onClick={handleSaveTest}>
						zapisz
						<Icon name="download" width="15" height="15" color={colors.white} />
					</Button>
					<Button
						color="secondary text-white"
						onClick={activeTest !== null ? openPrintModal : undefined}
					>
						drukuj
						<Icon name="print" width="15" height="15" color={colors.white} />
					</Button>
					{!!activeTest['id_testu'] && (
						<Button color="delete text-white" className="delete" onClick={openDeleteModal}>
							<Icon name="trash" width="12px" height="12px" color={colors.white} />
						</Button>
					)}
				</FixedButtons>
			)}
		</footer>
	);
};

export default TestEditorFooter;
